import { cloneElement, HTMLProps, KeyboardEvent, ReactElement, ReactNode, useState } from "react";
import { Tooltip, TooltipProps } from "@mui/material";

export type FormControlTooltipProps<WrapDisabledChild extends boolean | undefined = undefined> = {
	children: ReactElement;
	tooltip?: ReactNode;
	disabled?: boolean;
	disabledTooltip?: ReactNode;
	wrapDisabledChild?: WrapDisabledChild;
	slotProps?: {
		tooltip?: Omit<TooltipProps, "title" | "children">;
		tooltipWrapper?: WrapDisabledChild extends false ? undefined : HTMLProps<HTMLDivElement>;
	};
};

export default function FormControlTooltip<WrapDisabledChild extends boolean | undefined = undefined>({
	tooltip,
	disabled,
	disabledTooltip,
	wrapDisabledChild = true,
	slotProps,
	children,
}: FormControlTooltipProps<WrapDisabledChild>) {
	// MUI closes its own tooltip from a `keydown` listener on `document`, which sits above everything
	// else — so a modal around this trigger saw that same Escape and closed along with the tooltip.
	// Owning the open state lets us close the tooltip from the trigger's own React handler instead:
	// that runs below `document`, so stopPropagation there keeps the key from ever getting out. Closing
	// by blurring the trigger would work too, but inside a modal it hands focus to the focus trap, which
	// drops it on the modal's close button rather than leaving it on the trigger.
	const [tooltipOpen, setTooltipOpen] = useState(false);

	const resolvedDisabled = disabled ?? children.props.disabled ?? false;
	const wrappedChild =
		resolvedDisabled && wrapDisabledChild ? <div {...slotProps?.tooltipWrapper}>{children}</div> : children;

	// Only swallow Escape while the tooltip is actually showing, so it still reaches the modal when the
	// trigger merely has focus.
	const handleTriggerKeyDown = (event: KeyboardEvent) => {
		const { onKeyDown } = wrappedChild.props as { onKeyDown?: (event: KeyboardEvent) => void };
		onKeyDown?.(event);

		if (event.key !== "Escape" || !tooltipOpen) return;
		event.stopPropagation();
		setTooltipOpen(false);
	};

	if (tooltip || (resolvedDisabled && disabledTooltip)) {
		return (
			// Open state goes before the caller's slotProps so they can still control `open` themselves.
			<Tooltip
				open={tooltipOpen}
				onOpen={() => setTooltipOpen(true)}
				onClose={() => setTooltipOpen(false)}
				{...slotProps?.tooltip}
				title={resolvedDisabled && disabledTooltip ? disabledTooltip : tooltip}
			>
				{cloneElement(wrappedChild, { onKeyDown: handleTriggerKeyDown })}
			</Tooltip>
		);
	} else {
		return children;
	}
}
